import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import qs from 'query-string';
import strings from 'strings';
import { BulkMenuItems } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage, getRemainingTimeToDate } from 'UI/utils';

import API from '../API';

const { BulkEmails, BulkEmailsDrafts, BulkEmailById, BulkEmailScheduleById, BulkEmailSchedules } =
  Endpoints;
const {
  bulkEmails: {
    errors: bulkErrors,
    schedule: { errors: scheduleErrors }
  }
} = strings;

/**
 * @typedef {Object} Endpoints
 * @property {string} getAll
 * @property {string} getById
 */

/**
 * @param {'sent' | 'drafts' | 'schedule'} mode
 * @returns {{endpoints: Endpoints, statusAlert, errorAlert}}
 */
const getModeValues = mode => {
  const modesParams = {
    [BulkMenuItems.Sent]: {
      endpoints: {
        getAll: BulkEmails,
        getById: BulkEmailById
      },
      errorAlert: bulkErrors.gettingSentEmails
    },
    [BulkMenuItems.Drafts]: {
      endpoints: {
        getAll: BulkEmailsDrafts,
        getById: BulkEmailById
      },
      errorAlert: bulkErrors.gettingDrafts
    },
    [BulkMenuItems.Schedule]: {
      endpoints: {
        getAll: BulkEmailSchedules,
        getById: BulkEmailScheduleById
      },
      errorAlert: bulkErrors.gettingSchedules
    }
  };

  return modesParams[mode] || false;
};

/**
 * @typedef {'sent' | 'drafts' | 'schedule'} BulkListModes
 */

/**
 * Function that retrieves a list of sent emails
 * @param {Object} options
 * @param {Object} [options.queryParams]
 * @param {string} [options.queryParams.keyword]
 * @param {number} [options.queryParams.page]
 * @param {number} [options.queryParams.perPage]
 * @param {string} [options.queryParams.orderBy]
 * @param {'asc' | 'desc'} [options.queryParams.direction]
 * @param {Array} [options.queryParams.recruiterIds]
 * @param {Array} [options.queryParams.coachIds]
 * @param {Array} [options.queryParams.regionalIds]
 * @param {Array} [options.queryParams.scopeIds]
 * @param {Array} [options.queryParams.templateIds]
 * @param {number} [options.queryParams.userFilter]
 * @param {BulkListModes} options.mode
 * @returns {Promise}
 */
export const getBulks = async ({ queryParams = {}, mode }) => {
  const result = { success: false };
  const { endpoints, errorAlert } = getModeValues(mode);
  try {
    const { keyword, page, perPage, orderBy, direction, ...restFilters } = queryParams;
    const stringyfiedUrl = qs.stringifyUrl(
      {
        url: endpoints.getAll,
        query: {
          keyword: keyword || '',
          page: page || 1,
          perPage: perPage || 10,
          orderBy: orderBy || '',
          direction: direction || '',
          ...restFilters
        }
      },
      { arrayFormat: 'comma' }
    );
    const response = await API.get(stringyfiedUrl);
    if (response.status === HTTPStatusCodes.Ok) {
      result.success = true;
      result.data = response.data.data.map(item => ({
        ...item,
        warning:
          mode === BulkMenuItems.Schedule && !getRemainingTimeToDate(item?.send_date)
            ? scheduleErrors.emailCouldntBeSent
            : null
      }));
    }
  } catch (error) {
    result.alert = {
      severity: 'error',
      title: errorAlert,
      body: getErrorMessage(error)
    };
  }
  return result;
};

/**
 * @param {number} emailId
 * @param {BulkListModes} bulkMode
 * @returns {Promise}
 */
export const getBulkById = async (emailId, bulkMode) => {
  const { endpoints } = getModeValues(bulkMode);
  const result = { success: false };
  try {
    const response = await API.get(endpoints.getById.replace(/:id/, emailId));
    if (response.status === HTTPStatusCodes.Ok) {
      const {
        allow_candidates_in_marketing: allowMarketingCandidates,
        allow_hirings_in_recruiting: allowRecruitHirings,
        block_associated_companies: blockAssociatedCompanies,
        block_client_companies: blockClientCompanies,
        block_duration_days: blockDays,
        block_resend: blockResend,
        block_similar_companies: blockCompanies,
        bulkSequence,
        bulkType,
        created_at,
        created_by,
        emailBody,
        emailTemplate,
        marketingCandidates,
        prompt_id,
        recruitingJobOrders,
        search_project_selection_params: searchProjectSelectionParams,
        searchProject,
        send_date,
        signature,
        subcategory,
        use_outlook,
        user_email,
        user_name,
        wasScheduledEmail
      } = response.data;
      result.success = true;
      result.data = {
        createdAt: created_at,
        emailConfig: {
          bulkType,
          generalCategory: subcategory,
          isScheduled: wasScheduledEmail,
          template: emailTemplate || {},
          blockResend,
          blockDays,
          marketing: {
            blockAssociatedCompanies,
            blockCompanies,
            blockClientCompanies,
            allowMarketingCandidates,
            marketingCandidates
          },
          recruiting: {
            allowRecruitHirings,
            recruitingJobOrders
          },
          searchProjectSelectionParams,
          sendViaOutlook: use_outlook
        },
        emailBody: {
          ...emailBody,
          signature,
          attachments: emailBody?.attachments.map(({ file_size, ...rest }) => ({
            size: file_size,
            ...rest
          }))
        },
        searchProject: {
          id: searchProject?.id,
          name: searchProject?.name,
          bulkableItems: searchProject?.total_items
        },
        sendDate: send_date,
        sequences: bulkSequence,
        userInfo: {
          id: created_by,
          email: user_email,
          name: user_name
        }
      };

      if (prompt_id) result.data.promptId = prompt_id;
    }
  } catch (error) {
    result.alert = {
      severity: 'error',
      title: bulkErrors.gettingBulkById,
      body: getErrorMessage(error)
    };
  }
  return result;
};
